<script setup lang="ts">
// icons
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';

/********************
 * PROPS & EMITS    *
 ********************/
export interface CiMapCampsiteTileMobileProps {
  campsite: any;
  loading?: boolean;
  snowplowContext?: any;
}
const props = withDefaults(defineProps<CiMapCampsiteTileMobileProps>(), {
  loading: false,
  snowplowContext: () => {},
});

const emit = defineEmits<{
  'open-details': [];
}>();

/********************
 * COMPOSITIONS      *
 ********************/
const localePath = useLocalePath();
const { $getCampsiteNameWithCampsitePrefix } = useNuxtApp();
const { fixImageUrlAndCheckWebp } = useWebp();
const { mapRating } = useRating();
const { visibilityProducts } = useVisibilityProducts();
const { tileTrackAdClickAndFollow, getDetailQueryParams, hasImage, isVisible } = useCampsiteTile('CiMapCampsiteTileMobile', {
  campsite: () => props.campsite,
  snowplowContext: () => props.snowplowContext,
});
const { isFavorite, toggleFavorite } = useFavorites({ campsite: () => props.campsite });
const { impressionId, trackAdClick } = useTracking();

/********************
 * REFS & VARS       *
 ********************/
const i18nImageAlt = computed(() => {
  return $getCampsiteNameWithCampsitePrefix(props.campsite.name);
});

const fanclubBenefits = computed(() => {
  return props.campsite.fanclub?.benefits || 0;
});

/********************
 * COMPOSITIONS      *
 ********************/
const { $gettext, $ngettext, $pgettext } = useGettext();

/********************
 * FUNCTIONS         *
 ********************/
function getImage(image, webp = false) {
  const img = {
    width: 500,
    height: 500,
    url: image['1_1'],
  };
  if (!visibilityProducts(props.campsite).hasConversionOptimization) {
    img.url = image['4_3'];
    img.height = 375;
  }
  return {
    ...img,
    ...fixImageUrlAndCheckWebp(img.url, webp),
  };
}

function getSourceSets(imageUrl) {
  const img = {
    width: 500,
    height: 500,
  };
  return [
    {
      ...img,
      ...fixImageUrlAndCheckWebp(imageUrl, true),
    },
    {
      ...img,
      ...fixImageUrlAndCheckWebp(imageUrl, false),
    },
  ];
}

function getPicture(image) {
  const jpg = getImage(image);
  const webp = getImage(image, true);

  return [
    {
      srcset: `${webp.url} ${webp.width}w`,
      media: '(min-width: 0px)',
      type: webp.contentType,
    },
    {
      srcset: `${jpg.url} ${jpg.width}w`,
      media: '(min-width: 0px)',
      type: jpg.contentType,
    },
  ];
}

function openDetails() {
  emit('open-details');
  trackAdClick({
    bannerId: props.snowplowContext.bannerId,
    context: props.snowplowContext.contexts,
    impressionId: impressionId.value,
    targetUrl: 'modal',
    zoneId: 'load-details',
  });
}
</script>

<template>
  <div
    v-observe-visibility="{
      callback: isVisible,
      once: true,
      throttle: 500,
      intersection: {
        threshold: 0.7,
      },
    }"
  >
    <div class="container-fluid pt-4">
      <div class="row no-gutters">
        <div class="col-5">
          <div
            class="relative h-full overflow-hidden rounded-lg"
            :class="{
              'w-full bg-gray-40 pt-[75%]': !hasImage,
            }"
          >
            <!-- images -->
            <picture v-if="hasImage && props.campsite.images">
              <source
                v-for="(source, idx) of getPicture(props.campsite.images[0])"
                :key="idx"
                :srcset="source.srcset"
                :media="source.media"
                :type="source.type"
              />
              <img
                :alt="i18nImageAlt"
                :draggable="false"
                :src="getImage(props.campsite.images[0]).url"
                :width="getImage(props.campsite.images[0]).width"
                :height="getImage(props.campsite.images[0]).height"
                class="size-full rounded-lg"
                loading="lazy"
              />
            </picture>

            <picture v-if="hasImage && !props.campsite.images">
              <source
                v-for="(source, idx) of getSourceSets(props.campsite.image)"
                :key="idx"
                :srcset="source.srcset"
                :media="source.media"
                :type="source.type"
              />
              <img
                :alt="i18nImageAlt"
                :draggable="false"
                :src="fixImageUrlAndCheckWebp(props.campsite.image, false).url"
                :width="500"
                :height="500"
                class="absolute size-full"
                loading="lazy"
              />
            </picture>

            <!-- fallback image -->
            <img
              v-if="!hasImage"
              class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
              src="~/assets/svg/ci-bildmarke-white.svg"
              alt="no-image"
            />
            <div
              v-if="fanclubBenefits > 0"
              class="pointer-events-none absolute left-0 top-0 inline-flex p-[6px]"
            >
              <img
                src="~/assets/svg/fanclub-logo-small.svg"
                loading="lazy"
                alt="Fanclub logo"
              />
            </div>

            <!-- fav toggle -->
            <CiCampsiteFavorite
              :active="isFavorite"
              class="absolute right-4 top-4"
              :show-label="false"
              @click="toggleFavorite"
            />
          </div>
        </div>

        <div class="col-7">
          <div class="ml-4">
            <!-- location -->
            <ul
              class="m-0 list-none truncate p-0 text-gray-light"
              style="font-size: 0.625rem"
            >
              <li
                v-if="props.campsite.city"
                class="inline text-xs"
              >
                {{ props.campsite.city }}
              </li>
            </ul>

            <!-- cp name -->
            <nuxt-link
              class="line-clamp-2 text-xs font-medium text-black no-underline"
              :to="
                localePath({
                  name: RBN_CAMPSITE_DETAIL,
                  params: { identifier: props.campsite.slug },
                  query: getDetailQueryParams(),
                })
              "
              @click.capture="tileTrackAdClickAndFollow($event, 'campsite_name')"
            >
              {{ props.campsite.name }}
            </nuxt-link>

            <!-- rating / award -->
            <div class="order-4 mt-4 flex flex-col md:order-5 md:-ml-10 md:mt-auto">
              <!-- rating -->
              <div class="flex items-center">
                <CiRatingRadial
                  :rating="props.campsite.rating_avg_overall"
                  class="chart-wrapper size-12"
                  font-class="font-bold text-sm"
                />

                <div
                  :class="{ 'campsite-tile__no-rating': !props.campsite.rating_count }"
                  class="my-auto ml-2 flex flex-col truncate text-xs"
                >
                  <span
                    v-if="props.campsite.rating_count"
                    class="font-bold uppercase"
                  >{{
                    mapRating(props.campsite.rating_avg_overall)
                  }}</span>
                  <span
                    v-if="props.campsite.rating_count"
                    class="rating-count"
                  >
                    {{
                      $ngettext('%{count} rating', '%{count} ratings', props.campsite.rating_count, {
                        count: props.campsite.rating_count,
                      })
                    }}
                  </span>
                  <strong v-if="!props.campsite.rating_count">
                    <small class="rating-count uppercase text-dark">{{
                      $pgettext('Please keep short. Has little space.', 'No ratings yet')
                    }}</small>
                  </strong>
                </div>
              </div>

              <!-- award -->
              <div
                v-if="props.campsite.ci_award"
                class="mt-0 flex items-center text-sm sm:mt-2"
              >
                <div class="size-12">
                  <img
                    class="campsite-tile__award-logo"
                    src="~/assets/svg/award.svg"
                    alt="Camping.info Award"
                  />
                </div>
                <div class="ml-2 flex flex-col">
                  <span class="text-xs font-bold uppercase">Award</span>
                  <span>{{ props.campsite.ci_award }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- open details -->
    <div
      class="campsite-tile__mobile-toggle user-select-none flex cursor-pointer items-center justify-center py-4 text-xs text-primary hover:underline"
      @click="openDetails"
    >
      <span class="font-bold">{{ $gettext('Open details') }}</span>
      <CiAwesomeIcon
        :icon="faChevronDown"
        class="ml-2 rotate-180 fill-primary"
        ratio="0.6"
      />
    </div>
  </div>
</template>

<style></style>
